import React, { useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import axios from 'axios';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { AlertCircle, ArrowLeft } from 'lucide-react';
import Navigation from '../components/Navigation';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const ParentLogin = () => {
  const navigate = useNavigate();
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState('');
  const [isLoading, setIsLoading] = useState(false);

  const handleLogin = async (e) => {
    e.preventDefault();
    setError('');
    setIsLoading(true);

    try {
      const { data } = await axios.post(`${BACKEND_URL}/api/auth/login`, {
        identifier: email.trim(),
        password,
      });
      if (data.role !== 'parent') {
        setError(`This account is a ${data.role}. Please use the correct login page or try /login for the unified sign-in.`);
        setIsLoading(false);
        return;
      }
      Object.entries(data.storage || {}).forEach(([k, v]) => localStorage.setItem(k, v));
      localStorage.setItem('parent_user', JSON.stringify(data.user));
      navigate('/parent/dashboard');
    } catch (err) {
      setError(err.response?.data?.detail || 'Invalid email or password');
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-b from-blue-50 to-white">
      <Navigation />

      <Link
        to="/"
        className="fixed top-20 left-4 md:top-24 md:left-6 inline-flex items-center gap-1 text-gray-700 hover:text-blue-600 hover:underline font-medium min-h-[44px] z-40"
      >
        <ArrowLeft className="w-5 h-5" />
        Back to Home
      </Link>

      <div className="pt-32 pb-20 px-4">
        <div className="max-w-md mx-auto">
          <Card className="border-2 border-blue-200 shadow-xl">
            <CardContent className="p-8">
              <div className="text-center mb-8">
                <img 
                  src="https://customer-assets.emergentagent.com/job_sanjaya-ai-1/artifacts/8tjvhfc4_Gemini_Generated_Image_8jnxm88jnxm88jnx.png" 
                  alt="Sanjaya Logo" 
                  className="h-16 w-auto mx-auto mb-4"
                />
                <h1 className="text-3xl font-bold text-gray-900 mb-2">Parent Portal</h1>
                <p className="text-gray-600">Login to view your child's progress</p>
              </div>

              <form onSubmit={handleLogin} className="space-y-4" data-testid="parent-login-form">
                <div>
                  <Label>Email</Label>
                  <Input
                    type="email"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    placeholder="parent@example.com"
                    required
                    className="mt-2"
                    data-testid="parent-email-input"
                  />
                </div>

                <div>
                  <Label>Password</Label>
                  <Input
                    type="password"
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    placeholder="Enter your password"
                    required
                    className="mt-2"
                    data-testid="parent-password-input"
                  />
                </div>

                {error && (
                  <div className="bg-red-50 border border-red-200 rounded-lg p-3 flex items-start gap-2" data-testid="parent-login-error">
                    <AlertCircle className="w-5 h-5 text-red-600 flex-shrink-0 mt-0.5" />
                    <p className="text-sm text-red-800">{error}</p>
                  </div>
                )}

                <Button
                  type="submit"
                  disabled={isLoading}
                  className="w-full bg-gradient-to-r from-blue-500 to-blue-600 hover:from-blue-600 hover:to-blue-700 text-white py-6 text-lg"
                  data-testid="parent-login-submit"
                >
                  {isLoading ? 'Logging in...' : 'Login'}
                </Button>
              </form>

            </CardContent>
          </Card>
        </div>
      </div>
    </div>
  );
};

export default ParentLogin;
